import React, { useState, useEffect, useCallback, useMemo } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';

const ApiKeyManager = () => {
    const navigate = useNavigate();

    // 🔥 URL থেকে Username বের করা
    const queryParams = new URLSearchParams(window.location.search);
    const usernameFromUrl = queryParams.get('username');

    // 🔥 লোকাল স্টোরেজ থেকে মাস্টারি চেক এবং অ্যাডমিন আইডি নেওয়া
    const isMaster = localStorage.getItem('isMaster') === 'true';
    const loggedInUserId = localStorage.getItem('adminId');

    // --- Data States ---
    const [apiKeys, setApiKeys] = useState([]);
    const [users, setUsers] = useState([]);
    const [userSpecificDevices, setUserSpecificDevices] = useState([]); 
    const [devices, setDevices] = useState([]); 
    const [sims, setSims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // --- Modal & Form States ---
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingKey, setEditingKey] = useState(null);

    // --- Add Form State ---
    const initialAddFormState = {
        user_id: '',
        name: '',
        api_key: '',
        device_name: '',
        sim1: '',
        sim1_type: '',
        security_code: '',
        allowedIps: '',
    };
    const [addForm, setAddForm] = useState(initialAddFormState);

    // --- Data Fetcher ---
    const fetchData = useCallback(() => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        
        socketService.emit('fetchApiKeys');
        socketService.emit('fetchUsersList');
    }, []);

    // --- Socket Listeners ---
    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket || !socket.connected) {
            socketService.connect();
        }

        const handleConnect = () => {
            fetchData(); 
        };

        const handleApiKeysData = (data) => {
            setLoading(false);
            if (data.error) {
                setError(data.error);
                return;
            }
            const sortedKeys = (data.apiKeys || []).sort((a, b) => {
                if (a.username < b.username) return -1;
                if (a.username > b.username) return 1;
                return moment(b.created_at).valueOf() - moment(a.created_at).valueOf();
            });
            setApiKeys(sortedKeys);
        };
        
        const handleConfirmation = (event, data) => {
            setLoading(false);
            if (data.success) {
                setSuccessMessage(data.message || '✅ Operation successful!');
                fetchData();
            } else {
                setError(data.message || '❌ Failed: Unknown error');
            }
        };

        const handleToggleStatusResponse = (data) => handleConfirmation('apiKeyManager-toggle-status-response', data);

        const handleUserDeviceData = (data) => {
            if (data && data.devices) {
                const deviceNames = data.devices.map(d => d.deviceName);
                setUserSpecificDevices(deviceNames);
            } else {
                setUserSpecificDevices([]);
            }
        };
        
        socketService.on('connect', handleConnect); 
        socketService.on('apiKeysList', handleApiKeysData);
        socketService.on('usersList', (data) => setUsers(data || [])); 
        socketService.on('apiKeyManager-devicesList', (data) => setDevices(data || []));
        socketService.on('apiKeyManager-simsList', (data) => setSims(data || []));
        socketService.on('userDeviceManager-userData', handleUserDeviceData);

        const handleAddResponse = (data) => {
            if (data.success) {
                if (usernameFromUrl) {
                    // URL-e username thakle user_id o device keep koro, shudhu form fields reset
                    setAddForm(prev => ({
                        ...initialAddFormState,
                        user_id: prev.user_id,
                        device_name: prev.device_name,
                    }));
                } else if (!isMaster && loggedInUserId) {
                    setAddForm({ ...initialAddFormState, user_id: loggedInUserId });
                    socketService.emit('userDeviceManager-fetchUserAndDevices', { userId: loggedInUserId });
                } else {
                    setAddForm(initialAddFormState);
                    setUserSpecificDevices([]);
                }
            }
            handleConfirmation('apiKeyManager-add-response', data);
        };

        const handleEditResponse = (data) => {
            handleConfirmation('apiKeyManager-edit-response', data);
            setShowEditModal(false);
            setEditingKey(null);
        };

        const handleDeleteResponse = (data) => handleConfirmation('apiKeyManager-delete-response', data);
        const handleUsersListData = (data) => setUsers(data || []);
        const handleDevicesListData = (data) => setDevices(data || []);
        const handleSimsListData = (data) => setSims(data || []);

        socketService.on('apiKeyManager-add-response', handleAddResponse);
        socketService.on('apiKeyManager-edit-response', handleEditResponse);
        socketService.on('apiKeyManager-delete-response', handleDeleteResponse);
        socketService.on('apiKeyManager-toggle-status-response', handleToggleStatusResponse);
        socketService.on('globalApiKeyRefresh', fetchData);
        socketService.on('usersList', handleUsersListData);
        socketService.on('apiKeyManager-devicesList', handleDevicesListData);
        socketService.on('apiKeyManager-simsList', handleSimsListData);

        if (socket && socket.connected) {
            fetchData();
        }

        return () => {
            socketService.off('connect', handleConnect);
            socketService.off('apiKeysList', handleApiKeysData);
            socketService.off('usersList', handleUsersListData);
            socketService.off('apiKeyManager-devicesList', handleDevicesListData);
            socketService.off('apiKeyManager-simsList', handleSimsListData);
            socketService.off('userDeviceManager-userData', handleUserDeviceData);
            socketService.off('apiKeyManager-add-response', handleAddResponse);
            socketService.off('apiKeyManager-edit-response', handleEditResponse);
            socketService.off('apiKeyManager-delete-response', handleDeleteResponse);
            socketService.off('apiKeyManager-toggle-status-response', handleToggleStatusResponse);
            socketService.off('globalApiKeyRefresh', fetchData);
        };
    }, [fetchData, isMaster, loggedInUserId, usernameFromUrl]);


    // 🔥🔥 Effect 2: Auto Select User Logic (Critical Fix) 🔥🔥
   // 🔥🔥 Effect 2: Auto Select User Logic (Critical Fix) 🔥🔥
    useEffect(() => {
        // ১. সবার আগে চেক করতে হবে URL এ username আছে কিনা (Admin/Downline View)
        if (usernameFromUrl && users.length > 0) {
            const targetUser = users.find(u => u.username === usernameFromUrl);
            if (targetUser && addForm.user_id !== targetUser.id) {
                setAddForm(prev => ({ ...prev, user_id: targetUser.id }));
                socketService.emit('userDeviceManager-fetchUserAndDevices', { userId: targetUser.id });
            }
        }
        // ২. যদি URL এ username না থাকে এবং ইউজার মাস্টার না হয়, তবে তার নিজের আইডি সিলেক্ট হবে
        else if (!isMaster && loggedInUserId && users.length > 0) {
            const currentUser = users.find(u => u.id === loggedInUserId || u._id === loggedInUserId);
            
            if (currentUser && addForm.user_id !== currentUser.id) {
                setAddForm(prev => ({ ...prev, user_id: currentUser.id }));
                socketService.emit('userDeviceManager-fetchUserAndDevices', { userId: currentUser.id });
            }
        }
    }, [isMaster, loggedInUserId, usernameFromUrl, users]);

    // =========================================================
    // --- Handlers ---
    // =========================================================

    const handleAddFormChange = (e) => {
        const { name, value } = e.target;
        setAddForm(prev => ({ ...prev, [name]: value }));

        if (name === 'user_id') {
            setUserSpecificDevices([]); 
            setAddForm(prev => ({ ...prev, device_name: '' })); 
            
            if (value) {
                socketService.emit('userDeviceManager-fetchUserAndDevices', { userId: value });
            }
        }
    };

    const handleAddSubmit = (e) => {
        e.preventDefault();
        if (!addForm.user_id || !addForm.name || !addForm.api_key || !addForm.device_name) {
            alert('User, Name, API Key, and Device are required fields!');
            return;
        }
        setLoading(true);
        socketService.emit('apiKeyManager-add', { ...addForm, status: 'deactive' }); 
    };

    const handleEditClick = (keyData) => {
        setEditingKey({
            ...keyData,
            id: keyData._id || keyData.id,
            // array → কমা-সেপারেটেড string (input এ দেখানোর জন্য)
            allowedIps: Array.isArray(keyData.allowedIps) ? keyData.allowedIps.join(', ') : (keyData.allowedIps || '')
        });
        setDevices([]);
        setSims([]);
        
        if (socketService) {
            socketService.emit('apiKeyManager-getDevices', { username: keyData.username });
            if (keyData.device_name) { 
                socketService.emit('apiKeyManager-getSims', { username: keyData.username, device_name: keyData.device_name });
            }
        }
        setShowEditModal(true);
    };
    
    const handleDeviceChange = (e) => {
        const selectedDevice = e.target.value;
        setEditingKey(prev => ({ ...prev, device_name: selectedDevice, sim1: '' }));
        setSims([]); 
        if (socketService && selectedDevice && editingKey?.username) {
            socketService.emit('apiKeyManager-getSims', { 
                username: editingKey.username, 
                device_name: selectedDevice 
            });
        }
    };
    
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditingKey(prev => ({ ...prev, [name]: value }));
    };
    
    const handleEditSave = (e) => {
        e.preventDefault();
        if (!editingKey.api_key || !editingKey.username) {
            alert('Username and API Key are required.');
            return;
        }
        setLoading(true);
        socketService.emit('apiKeyManager-edit', editingKey);
    };

    const handleDelete = (id) => {
        if (!window.confirm('Are you sure you want to delete this API key?')) return;
        setLoading(true);
        socketService.emit('apiKeyManager-delete', { id });
    };

    const closeEditModal = () => {
        setEditingKey(null);
        setShowEditModal(false);
        setDevices([]);
        setSims([]);
    };
    
    const handleToggleStatus = (key) => {
        if (loading) return; 
        const newStatus = key.status === 'active' ? 'deactive' : 'active';
        setApiKeys(prevKeys => 
            prevKeys.map(k => k.id === key.id ? { ...k, status: newStatus } : k)
        );
        setLoading(true);
        socketService.emit('apiKeyManager-toggle-status', { 
            id: key.id, 
            status: newStatus 
        });
    };
    
    // 🔥🔥🔥 Group API Keys with Strict Filtering 🔥🔥🔥
    const groupedApiKeys = useMemo(() => {
        let filteredKeys = apiKeys;

        // ১. যদি URL এ username থাকে (Admin View)
        if (usernameFromUrl) {
            filteredKeys = apiKeys.filter(key => key.username === usernameFromUrl);
        }
        // ২. যদি Master না হয় (User View) - এখানে Strict Filter করা হচ্ছে
        else if (!isMaster && loggedInUserId) {
             // users লিস্ট থেকে কারেন্ট ইউজারের অবজেক্ট বের করা
             const currentUser = users.find(u => u.id === loggedInUserId || u._id === loggedInUserId);
             
             if (currentUser) {
                 // শুধু সেই ইউজারের username এর সাথে ম্যাচ করা কীগুলো দেখানো হবে
                 filteredKeys = apiKeys.filter(key => key.username === currentUser.username);
             } else {
                 // যদি ইউজার লোড না হয়ে থাকে বা খুঁজে না পাওয়া যায়, তবে সব হাইড করে দিন
                 // যাতে অন্য কারো ডাটা লিক না হয়
                 filteredKeys = [];
             }
        }

        return filteredKeys.reduce((acc, key) => {
            const groupName = key.username || 'Unknown User'; 
            if (!acc[groupName]) {
                acc[groupName] = [];
            }
            acc[groupName].push(key);
            return acc;
        }, {});
    }, [apiKeys, usernameFromUrl, isMaster, loggedInUserId, users]); 


    const pageStyles = `
        .content { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .nav-tabs-container { display: flex; background: #ffffff; padding: 15px 20px 0 20px; border-bottom: 1px solid #e2e8f0; margin-bottom: 20px; gap: 15px; border-radius: 12px 12px 0 0; }
        .nav-tab-item { padding: 10px 25px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; border: 1px solid transparent; border-radius: 6px; transition: all 0.2s ease; text-transform: uppercase; margin-bottom: 15px; }
        .nav-tab-item:hover { color: #0284c7; background: #f8fafc; }
        .nav-tab-item.active { color: #0284c7; border: 1px solid #0284c7; background: #f0f9ff; font-weight: 800; }
        .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
        .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; }
        .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; transition: 0.3s; border: none; }
        .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
        .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; }
        .custom-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.7); z-index: 99999 !important; display: flex; justify-content: center; align-items: flex-start; padding-top: 60px; overflow-y: auto; }
        .custom-modal-dialog { background: #fff; width: 90%; max-width: 550px; margin: 0 auto 50px auto; border-radius: 12px; box-shadow: 0 20px 40px rgba(0,0,0,0.4); position: relative; animation: slideUp 0.3s ease-out; display: flex; flex-direction: column; }
        .custom-modal-content { border: none; overflow: hidden; border-radius: 12px; width: 100%; }
        .modal-header { padding: 15px 20px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; background: #ffffff; border-radius: 12px 12px 0 0; }
        .modal-body { padding: 20px; overflow-x: hidden; }
        .modal-footer { padding: 15px 20px; border-top: none; display: flex; gap: 10px; background: #fff; border-radius: 0 0 12px 12px; }
        .btn-close { font-size: 12px; background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat; width: 1em; height: 1em; padding: 0.5em; opacity: 0.5; border: 0; cursor: pointer; }
        .btn-close:hover { opacity: 0.8; }
        @keyframes slideUp { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    `;

    return (
        <Layout>
            <style>{pageStyles}</style>

            <div className="content p-4">
                
                {usernameFromUrl && (
                    <div className="bg-white rounded-top shadow-sm mb-4">
                        <div className="nav-tabs-container mb-0">
                            <div className="nav-tab-item" onClick={() => navigate(`/payment_send?username=${usernameFromUrl}`)}>📤 Payment Send</div>
                            <div className="nav-tab-item" onClick={() => navigate(`/appreceive_user?username=${usernameFromUrl}`)}>📥 App Receive</div>
                            <div className="nav-tab-item" onClick={() => navigate(`/user_devices?username=${usernameFromUrl}`)}>📱 User Devices</div>
                            <div className="nav-tab-item" onClick={() => navigate(`/user_mobile_banking?username=${usernameFromUrl}`)}>💳 Mobile banking</div>
                            <div className="nav-tab-item active" onClick={() => {}}>⚙️ WD api config</div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center p-3">
                            <h5 className="section-title m-0">API Configuration: <span className="text-primary">{usernameFromUrl}</span></h5>
                            <div className="count-badge">Total Keys: <span>{groupedApiKeys[usernameFromUrl] ? groupedApiKeys[usernameFromUrl].length : 0}</span></div>
                        </div>
                    </div>
                )}
                
                {!usernameFromUrl && <h4 className="text-primary mb-3">🔑 API Key Manager</h4>}
                
                {successMessage && <div className="alert alert-success">{successMessage}</div>}
                {error && <div className="alert alert-danger">{error}</div>}
                
                <form onSubmit={handleAddSubmit} className="bg-white p-4 rounded shadow-sm mb-4">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Select User*</label>
                            {/* 🔥 ফিক্স: মাস্টার না হলে ড্রপডাউন ডিসেবল থাকবে এবং শুধু নিজের নাম দেখাবে */}
                            <select 
                                name="user_id" 
                                value={addForm.user_id} 
                                onChange={handleAddFormChange} 
                                className="form-select" 
                                required 
                                disabled={loading || !!usernameFromUrl || !isMaster} 
                            >
                                <option value="">Select User</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.username}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="col-md-3">
                            <label className="form-label">Name*</label>
                            <input type="text" name="name" value={addForm.name} onChange={handleAddFormChange} className="form-control" required disabled={loading}/>
                        </div>
                        
                        <div className="col-md-3">
                            <label className="form-label">API Key*</label>
                            <input type="text" name="api_key" value={addForm.api_key} onChange={handleAddFormChange} className="form-control" required disabled={loading}/>
                        </div>
                        
                        <div className="col-md-3">
                            <label className="form-label">Select Device*</label>
                            <select name="device_name" value={addForm.device_name} onChange={handleAddFormChange} className="form-select" required disabled={loading}>
                                <option value="">
                                    {addForm.user_id ? 'Select Device' : 'Select User First'}
                                </option>
                                {userSpecificDevices.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">SIM 1</label>
                            <input type="text" name="sim1" value={addForm.sim1} onChange={handleAddFormChange} className="form-control" placeholder="Optional" disabled={loading}/>
                        </div>
                        
                        <div className="col-md-3">
                            <label className="form-label">SIM 1 Type</label>
                            <input type="text" name="sim1_type" value={addForm.sim1_type} onChange={handleAddFormChange} className="form-control" placeholder="Optional" disabled={loading}/>
                        </div>
                        
                        <div className="col-md-3">
                            <label className="form-label">Security Code</label>
                            <input type="text" name="security_code" value={addForm.security_code} onChange={handleAddFormChange} className="form-control" placeholder="Optional" disabled={loading}/>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Allowed IPs <small className="text-muted">(কমা দিয়ে, খালি = সব IP)</small></label>
                            <input type="text" name="allowedIps" value={addForm.allowedIps} onChange={handleAddFormChange} className="form-control" placeholder="যেমন: 1.2.3.4, 5.6.7.8" disabled={loading}/>
                        </div>

                        <div className="col-md-3 align-self-end">
                            <button type="submit" className="btn btn-success w-100" disabled={loading}>➕ Add API Key</button>
                        </div>
                    </div>
                </form>

                {loading && <div className="text-center p-5">Loading API Keys...</div>}
                
                {!loading && !error && (
                    <div className="api-key-groups">
                        {Object.keys(groupedApiKeys).length > 0 ? (
                            Object.entries(groupedApiKeys).map(([groupName, keys]) => (
                                <div key={groupName} className="card mb-5 shadow border-primary border-3">
                                    <div className="card-header bg-primary text-white p-3">
                                        <h5 className="mb-0">
                                            👤 User: {groupName} 
                                            <span className="badge bg-dark ms-2">{keys.length} Keys</span>
                                        </h5>
                                    </div>
                                    
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-striped align-middle bg-white text-center mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>API Key</th>
                                                        <th>Device</th>
                                                        <th>SIM</th>
                                                        <th>Type</th>
                                                        <th>Security Code</th>
                                                        <th>Allowed IPs</th>
                                                        <th>Status</th>
                                                        <th>Created</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {keys.map((key) => {
                                                        const simDisplay = typeof key.sim1 === 'object' && key.sim1 !== null ? key.sim1.sim : key.sim1;
                                                        return (
                                                            <tr key={key.id} className={key.status === 'deactive' ? 'table-danger' : ''}> 
                                                                <td>{key.name}</td>
                                                                <td>{key.api_key}</td>
                                                                <td>{key.device_name || '-'}</td>
                                                                <td>{simDisplay || '-'}</td>
                                                                <td>{key.sim1_type || '-'}</td>
                                                                <td>{key.security_code || '-'}</td>
                                                                <td>
                                                                    {Array.isArray(key.allowedIps) && key.allowedIps.length > 0 ? (
                                                                        <div className="d-flex flex-column gap-1 align-items-center">
                                                                            {key.allowedIps.map((ip, i) => (
                                                                                <span key={i} className="badge bg-info text-dark" style={{ fontSize: '11px' }}>🔒 {ip}</span>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="badge bg-secondary" style={{ fontSize: '11px' }}>🌐 All IPs</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className={`badge ${key.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                                        {key.status ? key.status.toUpperCase() : 'N/A'}
                                                                    </span>
                                                                    <button className={`btn btn-sm ms-2 ${key.status === 'active' ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggleStatus(key)} disabled={loading}>{key.status === 'active' ? 'Deactivate' : 'Activate'}</button>
                                                                </td>
                                                                <td>{moment(key.created_at).format('YYYY-MM-DD HH:mm')}</td>
                                                                <td>
                                                                    <button className="btn btn-warning btn-sm editBtn me-2" onClick={() => handleEditClick(key)}>✏️ Edit</button>
                                                                    <button className="btn btn-danger btn-sm deleteBtn" onClick={() => handleDelete(key.id)}>Delete</button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="alert alert-info">No API keys found.</div>
                        )}
                    </div>
                )}
            </div>

            {showEditModal && editingKey && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-dialog">
                        <div className="custom-modal-content">
                            <div className="modal-header bg-light">
                                <h6 className="m-0 fw-bold">✏️ Edit API Key: {editingKey.username}</h6>
                                <button type="button" className="btn-close" onClick={closeEditModal}></button>
                            </div>
                            <form onSubmit={handleEditSave}>
                                <div className="modal-body p-4">
                                    <input type="hidden" name="id" value={editingKey.id || ''} />
                                    <input type="hidden" name="username" value={editingKey.username || ''} />
                                    <div className="mb-3"><label className="field-label">Name</label><input type="text" name="name" value={editingKey.name || ''} onChange={handleEditFormChange} className="modern-input" required /></div>
                                    <div className="mb-3"><label className="field-label">API Key</label><input type="text" name="api_key" value={editingKey.api_key || ''} onChange={handleEditFormChange} className="modern-input" required /></div>
                                    <div className="mb-3"><label className="field-label">Status</label><select name="status" value={editingKey.status || 'deactive'} onChange={handleEditFormChange} className="modern-input" required><option value="active">Active</option><option value="deactive">Deactive</option></select></div>
                                    <div className="mb-3"><label className="field-label">Device Name</label><select name="device_name" value={editingKey.device_name || ''} onChange={handleDeviceChange} className="modern-input" required><option value="">-- Select Device --</option>{devices.map(d => (<option key={d} value={d}>{d}</option>))}</select></div>
                                    <div className="mb-3"><label className="field-label">SIM Number</label><select name="sim1" value={editingKey.sim1 || ''} onChange={handleEditFormChange} className="modern-input"><option value="">-- Select SIM --</option>{sims.map((s, index) => { const simValue = typeof s === 'object' && s !== null ? s.sim : s; return (<option key={index} value={simValue}>{simValue}</option>); })}</select></div>
                                    <div className="row"><div className="col-6 mb-3"><label className="field-label">SIM Type</label><input type="text" name="sim1_type" value={editingKey.sim1_type || ''} onChange={handleEditFormChange} className="modern-input" /></div><div className="col-6 mb-3"><label className="field-label">Security Code</label><input type="text" name="security_code" value={editingKey.security_code || ''} onChange={handleEditFormChange} className="modern-input" /></div></div>
                                    <div className="row"><div className="col-12 mb-3"><label className="field-label">Allowed IPs <small className="text-muted">(কমা দিয়ে, খালি = সব IP allowed)</small></label><input type="text" name="allowedIps" value={editingKey.allowedIps || ''} onChange={handleEditFormChange} className="modern-input" placeholder="যেমন: 1.2.3.4, 5.6.7.8" /></div></div>
                                </div>
                                <div className="modal-footer"><button type="button" className="btn btn-reset flex-fill py-2" onClick={closeEditModal}>Cancel</button><button type="submit" className="btn btn-apply flex-fill py-2">Save Changes</button></div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            
        </Layout>
    );
};

export default ApiKeyManager;